import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { navigate } from "raviger";
import { Field, Form, Formik, } from 'formik';
import Select from 'react-select';
import { Switch } from 'formik-mui';
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Form as BootstrapForm,
  FormGroup,
  Row,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft,
} from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import { AuthContext } from "../accounts/AccountsReducer";
import { DateTimePicker, DropDown, TextInput } from '../components/Form';
import { SystemErrorContext } from '../components/SystemError';
import Patient from './components/Patient';

const customStyles = {
  // For the select it self, not the options of the select
  control: (styles, { isDisabled}) => {
    return {
      ...styles,
      color: '#FFF',
      cursor: isDisabled ? 'not-allowed' : 'default',
      backgroundColor: isDisabled ? '#DFDDDD' : 'white',
      // height: 35,
      minHeight: 35
    }
  },
  option: provided => ({
    ...provided,
    color: 'black'
  }),
  // singleValue: (styles, { isDisabled }) => ({
  //   ...styles,
  //   color: isDisabled ? '#595959' : 'black'
  // }),
};

const VetRequestForm = (props) => {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  // Determine if we're in the vet exam workflow.
  var is_workflow = window.location.pathname.endsWith("/workflow");

  const initialData = {
    open: new Date(),
    patient: props.animalid,
    incident: state.incident.id,
    requested_by: state.user.vet_perms ? state.user.id : null,
    exam: null,
    concern: '',
    priority: 'urgent',
    presenting_complaints: [],
    complaints_other: '',
    caution: false,
    incident_slug: props.incident,
    organization_slug: props.organization,
  }

  let current_data = initialData;
  if (is_workflow) {
    current_data = props.state.steps.vet_request;
    current_data['requested_by'] = state.user.id || null;
  }

  const [data, setData] = useState(current_data);

  const [animalData, setAnimalData] = useState({});
  const [presentingComplaintChoices, setPresentingComplaintChoices] = useState([]);
  const [assigneeChoices, setAssigneeChoices] = useState([]);

  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();
    if (props.id) {
      const fetchVetRequest = async () => {
        // Fetch Visit Note data.
        await axios.get('/vet/api/incident/' + (state ? state.incident.id : 'undefined') + '/vetrequest/' + props.id + '/', {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            setData(response.data);
            setAnimalData(response.data.animal_object)
          }
        })
        .catch(error => {
          setShowSystemError(true);
        });
      };
      fetchVetRequest();
    };

    if (props.animalid) {
      const fetchAnimalData = async () => {
        // Fetch Animal data.
        await axios.get('/animals/api/incident/' + (state ? state.incident.id : 'undefined') + '/animal/' + (props.animalid ? props.animalid : data.animal_object.id_for_incident) + '/?medical=true&light=true', {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            setAnimalData(response.data);
          }
        })
        .catch(error => {
          if (!unmounted) {
            if (axios.isCancel(error)) {
              console.log('Request canceled gracefully:', error.message);
            } else if (error.response)
            {
              setShowSystemError(true);
            }
          }
        })
      };
      fetchAnimalData();
    }
    else if (props.medrecordid) {
      const fetchMedRecord = async () => {
        // Fetch MedRecord data.
        await axios.get('/vet/api/medrecord/' + props.medrecordid + '/', {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            setAnimalData(response.data.animal_object);
          }
        })
        .catch(error => {
          if (axios.isCancel(error)) {
            console.log('Request canceled gracefully:', error.message);
          } else if (error.response)
          {
            setShowSystemError(true);
          }
        });
      };
      fetchMedRecord();
    }

    const fetchPresentingComplaints = async () => {
      // Fetch assignee data.
      await axios.get('/vet/api/complaints/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let options = [];
          response.data.forEach(function(complaint) {
            options.push({value: complaint.id, label: complaint.name})
          });
          setPresentingComplaintChoices(options);
        }
      })
      .catch(error => {
        if (axios.isCancel(error)) {
          console.log('Request canceled gracefully:', error.message);
        } else if (error.response)
        {
          setShowSystemError(true);
        }
      });
    };
    fetchPresentingComplaints();

    const fetchAssignees = async () => {
      // Fetch assignee data.
      await axios.get('/accounts/api/user/?vet=true&organization=' + state.organization.id, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let options = [];
          response.data.forEach(function(person) {
            options.unshift({value: person.id, label: person.first_name + ' ' + person.last_name})
          });
          setAssigneeChoices(options);
        }
      })
      .catch(error => {
        if (axios.isCancel(error)) {
          console.log('Request canceled gracefully:', error.message);
        } else if (error.response)
        {
          setShowSystemError(true);
        }
      });
    };
    fetchAssignees();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [props.id, props.animalid]);

  return (
    <Formik
      initialValues={data}
      enableReinitialize={true}
      validationSchema={Yup.object({
        concern: Yup.string().max(200, 'Must be 200 characters or less'),
        priority: Yup.string(),
        caution: Yup.boolean(),
        presenting_complaints: Yup.array().min(1, 'Required'),
        complaints_other: Yup.string().max(50, 'Must be 50 characters or less'),
        requested_by: Yup.string().nullable().required('Required'),
      })}
      onSubmit={(values, { setSubmitting }) => {
        if (data.id) {
          axios.put('/vet/api/vetrequest/' + data.id + '/', values)
          .then(response => {
            if (is_workflow) {
              props.onSubmit('vet_request', values, 'exam');
            }
            else {
              navigate('/' + props.organization + '/' + props.incident + '/vet/medrecord/' + data.medical_record);
            }
          })
          .catch(error => {
            setShowSystemError(true);
          });
          setSubmitting(false);
        }
        else {
          values["incident_slug"] = props.incident;
          values["organization_slug"] = props.organization;
          values["patient"] = animalData.id_for_incident;
          values["incident"] = state.incident.id;
          axios.post('/vet/api/vetrequest/', values)
          .then(response => {
            if (is_workflow) {
              window.history.replaceState("", "", window.location.pathname + "?vetrequest_id=" + response.data.id_for_incident)
              props.onSubmit('vet_request', response.data, 'exam');
            }
            else {
              navigate('/' + props.organization + '/' + props.incident + '/vet/medrecord/' + response.data.medical_record);
            }
          })
          .catch(error => {
            setShowSystemError(true);
          });
          setSubmitting(false);
        }
      }}
    >
      {formikProps => (
        <Card border="secondary" className="mt-3">
          <Card.Header as="h5" className="ps-3">
            {is_workflow ? <span style={{ cursor: 'pointer' }} onClick={() => navigate('/' + props.organization + '/' + props.incident + '/vet/medrecord/' + props.medrecordid + '/')} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
            : state.prevLocation ?
              <span style={{ cursor: 'pointer' }} onClick={() => navigate(state.prevLocation)} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
            : props.id ? <span style={{ cursor: 'pointer' }} onClick={() => navigate('/' + props.organization + '/' + props.incident + '/vet/vetrequest/' + props.id + '/')} className="me-3">
              <FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse />
            </span>:
            <span style={{ cursor: 'pointer' }} onClick={() => navigate('/' + props.organization + '/' + props.incident + '/animals/' + props.animalid + '/')} className="me-3">
              <FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse />
            </span>}
            {!props.id ? "" : "Update "}Veterinary Request Form
          </Card.Header>
          <Patient animal={animalData} vet_request={null} organization={props.organization} incident={props.incident} />
          <Card.Body>
            <Form>
              <FormGroup>
                <Row className="gx-2">
                  <Col xs={"4"}>
                    {assigneeChoices.length > 0 ? <DropDown
                      label="Requested By*"
                      id="requested_by"
                      name="requested_by"
                      type="text"
                      options={assigneeChoices}
                      isClearable={false}
                      onChange={(instance) => {
                        formikProps.setFieldValue("requested_by", instance === null ? '' : instance.value);
                      }}
                    /> : ""}
                  </Col>
                </Row>
                <Row className="mt-3 gx-2">
                  <Col>
                    <DateTimePicker
                      label="Scheduled"
                      name="open"
                      id="open"
                      xs="4"
                      onChange={(date, dateStr) => {
                        formikProps.setFieldValue("open", dateStr)
                      }}
                      data-enable-time={false}
                      clearable={false}
                      value={formikProps.values.open||new Date()}
                    />
                  </Col>
                </Row>
                <Row className="mt-3 gx-2">
                  <Col xs={"4"}>
                    <DropDown
                      label="Priority"
                      id="priorityDropdown"
                      name="priority"
                      type="text"
                      options={[
                        { value: 'urgent', label: 'Urgent (Red)' },
                        { value: 'when_available', label: 'When Available (Yellow)' },
                      ]}
                      value={formikProps.values.priority||data.priority}
                      isClearable={false}
                      onChange={(instance) => {
                        formikProps.setFieldValue("priority", instance === null ? '' : instance.value);
                      }}
                    />
                  </Col>
                </Row>
                <Row className="mt-3 mb-3">
                  <Col xs={"8"}>
                    <label className="mb-2">Presenting Complaints*</label>
                    <Select
                      id="presenting_complaintsDropdown"
                      name="presenting_complaints"
                      type="text"
                      styles={customStyles}
                      isMulti
                      options={presentingComplaintChoices}
                      value={presentingComplaintChoices.filter(choice => formikProps.values.presenting_complaints.includes(choice.value))}
                      isClearable={true}
                      onChange={(instance) => {
                        let values = [];
                        instance && instance.forEach(option => {
                          values.push(option.value);
                        })
                        formikProps.setFieldValue("presenting_complaints", instance === null ? [] : values);
                      }}
                    />
                    {formikProps.errors['presenting_complaints'] ? <div style={{ color: "#e74c3c", marginTop: ".5rem", fontSize: "80%" }}>{formikProps.errors['presenting_complaints']}</div> : ""}
                  </Col>
                </Row>
                {presentingComplaintChoices.length && formikProps.values.presenting_complaints.includes(presentingComplaintChoices.filter(option => option.label === 'Other')[0].value) ?
                <Row className="mb-3">
                  <TextInput
                    type="text"
                    label="Other Presenting Complaint"
                    name="complaints_other"
                    id="complaints_other"
                    xs="6"
                  />
                </Row>
                : ""}
                <Row className="ps-0">
                  <TextInput
                    as="textarea"
                    label="Concern"
                    name="concern"
                    id="concern"
                    xs="8"
                    rows={4}
                  />
                </Row>
                <Row className="mt-3" style={{marginBottom:"-15px"}}>
                  <Col xs="2">
                    <BootstrapForm.Label htmlFor="caution" style={{marginBottom:"-5px"}}>Use Caution</BootstrapForm.Label>
                    <div style={{marginLeft:"-3px"}}><Field component={Switch} name="caution" type="checkbox" color="primary" /></div>
                  </Col>
                </Row>
              </FormGroup>
            </Form>
          </Card.Body>
          <ButtonGroup>
            <Button type="button" className="btn btn-primary" onClick={() => { formikProps.submitForm() }}>{is_workflow ? "Next Step" : "Save"}</Button>
          </ButtonGroup>
        </Card>
      )}
    </Formik>
  );
};

export default VetRequestForm;
